import { PERSON_GENDERS } from "@seagames/contracts";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { PageSkeleton } from "../../components/accommodation/page-skeleton";
import { ATHLETE_ROLE_FILTERS, EVENT_FILL_FILTERS } from "../../components/shortlist/model";
import { type ShortlistSportSearch, ShortlistSportPage } from "../../components/shortlist/sport-page";
import { useDefaultGamesId } from "../../lib/competition/games";
import { searchText } from "../../lib/list-page/list-search";

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);

/**
 * One sport of one organisation's shortlist (M7a): a full page with the back
 * arrow. URL = the view (By event | By athlete), the event picked, By event's
 * fill filter and the By athlete filters; every param lenient. Unsaved changes are page state.
 */
export const Route = createFileRoute("/_app/accreditation/shortlist/$sportId")({
	validateSearch: z.object({
		games_id: lenient(z.string().uuid()),
		org: lenient(z.string().uuid()),
		view: lenient(z.enum(["athlete"])),
		event: lenient(z.string().uuid()),
		fill: lenient(z.enum(EVENT_FILL_FILTERS)),
		q: lenient(searchText()),
		role: lenient(z.enum(ATHLETE_ROLE_FILTERS)),
		gender: lenient(z.enum(PERSON_GENDERS)),
	}),
	staticData: { contentTop: "tabbed" },
	component: SportPage,
});

function SportPage() {
	const { sportId } = Route.useParams();
	const search = Route.useSearch() as ShortlistSportSearch;
	const navigate = Route.useNavigate();
	const gamesId = useDefaultGamesId(search.games_id);
	if (!gamesId) return <PageSkeleton />;
	return (
		<ShortlistSportPage
			key={`${sportId}|${search.org ?? ""}|${gamesId}`}
			gamesId={gamesId}
			sportId={sportId}
			search={search}
			patch={(next) => void navigate({ search: (prev) => ({ ...prev, ...next }), replace: true })}
		/>
	);
}
